import { motion } from "motion/react"

const PRODUCT_ICONS: Record<string, string> = {
  megastore: "fa-store",
  loon: "fa-music",
  vendekronia: "fa-stopwatch",
}

interface ProductCardProps {
  productKey: string
  name: string
  baseline: string
  description: string
  features: string[]
  url: string
  index: number
}

export default function ProductCard({
  productKey,
  name,
  baseline,
  description,
  features,
  url,
  index,
}: ProductCardProps) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.6, delay: index * 0.15 }}
      className="group relative flex flex-col p-8 bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl hover:border-cyan-500/30 transition-all duration-300"
    >
      {/* Top glow */}
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-cyan-500/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

      {/* Icon + Name */}
      <div className="flex items-center gap-4 mb-4">
        <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-cyan-500/10 text-cyan-400 shrink-0">
          <i className={`fa-solid ${PRODUCT_ICONS[productKey] ?? "fa-cube"} text-lg`} />
        </div>
        <div>
          <h3 className="text-xl font-bold text-white">{name}</h3>
          <p className="text-xs text-cyan-400 font-medium">{baseline}</p>
        </div>
      </div>

      {/* Description */}
      <p className="text-sm text-slate-400 leading-relaxed mb-6 flex-1">{description}</p>

      {/* Features */}
      <ul className="mb-6 space-y-2">
        {features.map((feature, i) => (
          <li key={i} className="flex items-center gap-2 text-sm text-slate-300">
            <span className="w-1 h-1 bg-cyan-400 rounded-full shrink-0" />
            {feature}
          </li>
        ))}
      </ul>

      {/* CTA */}
      <a
        href={url}
        className="inline-flex items-center gap-2 text-sm font-medium text-cyan-400 hover:text-cyan-300 transition-colors group/link"
      >
        En savoir plus
        <i className="fa-solid fa-arrow-right text-xs transition-transform group-hover/link:translate-x-1" />
      </a>
    </motion.div>
  )
}
